import { useState } from "react";
import img1 from "@assets/images/Rectangle-49@2x.png";
import img2 from "@assets/images/Rectangle-491@2x.png";
import img3 from "@assets/images/Rectangle-492@2x.png";
import { Reveal } from "@components/ui";

const categories = [
  "All Categories",
  "Safety Alerts",
  "Tariff & Billing Updates",
  "Service Announcements",
  "Maintenance Notices",
  "Community & CSR",
  "Corporate News",
  "Technical Bulletins",
  "Press Releases",
] as const;

interface Article {
  image: string;
  date: string;
  tag: string;
  tagColor: string;
  tagTextColor: string;
  title: string;
  category: string;
  categoryColor: string;
  excerpt: string;
}

const articles: Article[] = [
  {
    image: img1,
    date: "November 20, 2025",
    tag: "Residential",
    tagColor: "#c9e0ff",
    tagTextColor: "#003481",
    title: "URGENT: Gas Leak Protocol Reminder for All Customers",
    category: "Safety Alerts",
    categoryColor: "#f03813",
    excerpt:
      "A critical reminder to all customers about the proper steps to follow when you suspect a gas leak in your home or building.",
  },
  {
    image: img2,
    date: "November 20, 2025",
    tag: "Residential",
    tagColor: "#c9e0ff",
    tagTextColor: "#003481",
    title: "Updated Residential Tariff Rate \u2013 AED 35 per m\u00B3",
    category: "Tariff & Billing Updates",
    categoryColor: "#5f8100",
    excerpt:
      "Effective January 2026, the residential gas tariff will be adjusted. Learn what this means for your monthly bill.",
  },
  {
    image: img3,
    date: "November 20, 2025",
    tag: "Residential",
    tagColor: "#c9e0ff",
    tagTextColor: "#003481",
    title: "Smart Meter Rollout \u2013 Phase 2 Announced",
    category: "Tariff & Billing Updates",
    categoryColor: "#5f8100",
    excerpt:
      "Phase 2 of GASIS smart meter deployment begins in Q1 2026, covering Dubai Investment Park and surrounding communities.",
  },
  {
    image: img1,
    date: "November 20, 2025",
    tag: "Commercial",
    tagColor: "#d4f5e9",
    tagTextColor: "#00866f",
    title: "Scheduled Network Maintenance \u2013 Dubai Investment Park",
    category: "Maintenance Notices",
    categoryColor: "#00866f",
    excerpt:
      "Planned maintenance will affect gas supply in select areas of DIP. Check if your building is included.",
  },
  {
    image: img2,
    date: "November 20, 2025",
    tag: "Community",
    tagColor: "#fff3d6",
    tagTextColor: "#816300",
    title: 'GASIS Launches Annual "Gas Safety Awareness Week"',
    category: "Community & CSR",
    categoryColor: "#816300",
    excerpt:
      "Join us for a week of free workshops, demonstrations, and community events focused on gas safety education.",
  },
  {
    image: img3,
    date: "November 20, 2025",
    tag: "Corporate",
    tagColor: "#dce8f8",
    tagTextColor: "#003481",
    title: "GASIS Secures New Contract with Major Developer in Dubai",
    category: "Press Releases",
    categoryColor: "#003481",
    excerpt:
      "A landmark agreement to provide end-to-end gas infrastructure for a new mega-development in Dubai South.",
  },
];

function NewsArticleCard({ article }: { article: Article }) {
  return (
    <article className="group cursor-pointer flex flex-col h-full rounded-[24px] border border-[rgba(98,99,108,0.15)] bg-white overflow-hidden hover:shadow-[0_12px_40px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-300">
      {/* Image */}
      <div className="relative w-full overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-[200px] md:h-[260px] lg:h-[300px] object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {/* Category badge overlay */}
        <span
          className="absolute top-4 left-4 text-[12px] md:text-[13px] font-semibold tracking-[-0.02em] px-3 py-1.5 rounded-full text-white backdrop-blur-sm"
          style={{ backgroundColor: `${article.categoryColor}dd` }}
        >
          {article.category}
        </span>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-5 md:p-6 gap-3">
        {/* Meta row */}
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] md:text-[14px] text-[#62636c] tracking-[-0.02em]">
            {article.date}
          </span>
          <span className="w-1 h-1 rounded-full bg-[#b9bbc6]" />
          <span
            className="text-[12px] md:text-[13px] font-medium rounded-full px-2.5 py-1"
            style={{
              backgroundColor: article.tagColor,
              color: article.tagTextColor,
            }}
          >
            {article.tag}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-[18px] md:text-[22px] lg:text-[24px] font-semibold text-[#090d2c] tracking-[-0.04em] leading-[1.25] group-hover:text-[#122d63] transition-colors duration-200">
          {article.title}
        </h3>

        {/* Excerpt */}
        <p className="text-[14px] md:text-[16px] text-[#62636c] tracking-[-0.02em] leading-relaxed line-clamp-2">
          {article.excerpt}
        </p>

        {/* Read more */}
        <div className="mt-auto pt-3 border-t border-[rgba(98,99,108,0.1)]">
          <span className="inline-flex items-center gap-1.5 text-[14px] md:text-[15px] font-medium text-[#122d63] group-hover:text-[#f03813] transition-colors duration-200">
            Read Article
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transition-transform duration-200 group-hover:translate-x-1"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </article>
  );
}

export default function NewsCategoriesSection() {
  const [activeCategory, setActiveCategory] =
    useState<string>("All Categories");

  const filtered =
    activeCategory === "All Categories"
      ? articles
      : articles.filter((a) => a.category === activeCategory);

  return (
    <section className="relative w-full bg-[#fefcfc]">
      <div className="py-[60px] md:py-[80px] lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
        <div className="w-full max-w-[1368px] mx-auto flex flex-col gap-8 md:gap-10">
          {/* Category Tabs */}
          <Reveal>
            <div className="overflow-x-auto scrollbar-hide -mx-5 px-5 md:mx-0 md:px-0">
              <div className="flex md:flex-wrap gap-1 min-w-max md:min-w-0">
                {categories.map((cat) => {
                  const isActive = activeCategory === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`relative whitespace-nowrap px-4 py-3 text-[14px] md:text-[16px] lg:text-[18px] tracking-[-0.04em] font-albert-sans transition-all duration-200 rounded-full ${
                        isActive
                          ? "bg-[#f03813] text-white font-semibold shadow-[0_2px_12px_rgba(240,56,19,0.3)]"
                          : "text-[rgba(18,45,99,0.7)] hover:bg-[#122d63]/5 hover:text-[#122d63]"
                      }`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>
          </Reveal>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((article, index) => (
              <Reveal
                key={`${article.title}-${index}`}
                className="h-full"
                delay={index * 60}
              >
                <NewsArticleCard article={article} />
              </Reveal>
            ))}
          </div>

          {/* Empty state */}
          {filtered.length === 0 && (
            <Reveal>
              <div className="text-center py-16 rounded-[24px] border border-dashed border-[rgba(98,99,108,0.3)] bg-white">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#b9bbc6"
                  strokeWidth="1.5"
                  className="mx-auto mb-4"
                >
                  <path
                    d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="14 2 14 8 20 8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="text-[18px] text-[#62636c] font-albert-sans">
                  No articles found in this category yet.
                </p>
                <button
                  onClick={() => setActiveCategory("All Categories")}
                  className="mt-3 text-[15px] text-[#f03813] font-medium hover:underline"
                >
                  View all articles
                </button>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
